import { z } from 'zod';

export const SYSTEM_LOG_EXPORT_LIMIT = 10_000;
export const SYSTEM_LOG_SORT_FIELDS = Object.freeze([
    'createdAt',
    'action',
    'resource',
    'category',
    'severity',
    'status',
]);
export const SYSTEM_LOG_ACTION_FAMILIES = Object.freeze([
    'read', 'write', 'viewed', 'listed', 'created', 'updated', 'deleted', 'other',
]);

const boundedString = (max) => z.string().trim().min(1).max(max);
const optionalQueryString = (max) => z.preprocess(
    (value) => (value == null || String(value).trim() === '' ? undefined : value),
    boundedString(max).optional(),
);
const isoUtcInstant = z.string()
    .datetime({ offset: false })
    .refine((value) => value.endsWith('Z'), 'Must be an ISO UTC instant ending in Z');
const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ObjectId');
const actionFamily = z.enum(SYSTEM_LOG_ACTION_FAMILIES);
const actionFamilies = z.preprocess((value) => {
    if (value == null || value === '') return undefined;
    if (Array.isArray(value)) return value;
    return String(value).split(',').map((item) => item.trim()).filter(Boolean);
}, z.array(actionFamily).max(SYSTEM_LOG_ACTION_FAMILIES.length).optional());

const systemLogFilterShape = {
    search: optionalQueryString(200),
    action: optionalQueryString(200),
    actionMode: z.preprocess((value) => value || undefined, z.enum(['contains', 'exact']).optional()),
    actionFamily: z.preprocess(
        (value) => (!value || value === 'all' ? undefined : value),
        actionFamily.optional(),
    ),
    actionFamilies,
    resource: optionalQueryString(120),
    resourceId: optionalQueryString(200),
    user: optionalQueryString(320),
    status: z.preprocess((value) => value || undefined, z.enum(['success', 'failure']).optional()),
    severity: z.preprocess(
        (value) => value || undefined,
        z.enum(['info', 'warning', 'error', 'critical']).optional(),
    ),
    category: optionalQueryString(80),
    ip: optionalQueryString(64),
    requestId: optionalQueryString(200),
    sessionId: optionalQueryString(200),
    dateFrom: z.preprocess((value) => value || undefined, isoUtcInstant.optional()),
    dateTo: z.preprocess((value) => value || undefined, isoUtcInstant.optional()),
    dateToInclusive: z.preprocess(
        (value) => value === 'true' ? true : value === 'false' ? false : value,
        z.boolean().optional().default(true),
    ),
    selectedLogIds: z.array(objectId).max(SYSTEM_LOG_EXPORT_LIMIT).optional(),
    sortBy: z.preprocess(
        (value) => value || undefined,
        z.enum(SYSTEM_LOG_SORT_FIELDS).optional().default('createdAt'),
    ),
    sortOrder: z.preprocess(
        (value) => value || undefined,
        z.enum(['asc', 'desc']).optional().default('desc'),
    ),
};

export const SystemLogFiltersSchema = z.object(systemLogFilterShape).strict().superRefine((value, ctx) => {
    if (value.dateFrom && value.dateTo && Date.parse(value.dateFrom) > Date.parse(value.dateTo)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dateTo'],
            message: 'dateTo must be at or after dateFrom',
        });
    }
});

export const SystemLogListQuerySchema = z.object({
    ...systemLogFilterShape,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    // Kept for the existing Workflow caller; pageSize remains the canonical list bound.
    limit: z.coerce.number().int().min(1).max(100).optional(),
}).strict().superRefine((value, ctx) => {
    if (value.dateFrom && value.dateTo && Date.parse(value.dateFrom) > Date.parse(value.dateTo)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dateTo'], message: 'dateTo must be at or after dateFrom' });
    }
});

export const SystemLogExportFormatSchema = z.enum(['json', 'csv']);
export const SystemLogExportRequestSchema = z.object({
    filters: SystemLogFiltersSchema.optional().default({}),
    format: SystemLogExportFormatSchema.optional().default('json'),
}).strict();
export const SystemLogExportEstimateRequestSchema = z.object({
    filters: SystemLogFiltersSchema.optional().default({}),
}).strict();
export const SystemLogExportEstimateSchema = z.object({
    matchedCount: z.number().int().nonnegative(),
    exportableCount: z.number().int().nonnegative().max(SYSTEM_LOG_EXPORT_LIMIT),
    limit: z.literal(SYSTEM_LOG_EXPORT_LIMIT),
    truncated: z.boolean(),
}).strict();

// Legacy entity schemas remain exported for existing consumers.
export const SystemLogsSchema = z.object({
    id: z.string().optional(),
    level: z.string().min(1),
    message: z.string().min(1),
    timestamp: z.string().or(z.date()).optional(),
});
export const CreateSystemLogsSchema = SystemLogsSchema.omit({ id: true });
export const UpdateSystemLogsSchema = SystemLogsSchema;
